import type { IObservableArray } from 'mobx';
import { computed, makeObservable, toJS } from 'mobx';

import { concatPath, toQueryString } from '@feathr/hooks';
import type {
  IBaseAttributes,
  IListOptions,
  IListParams,
  IRachisMessage,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

import type { IAddress } from '../address';
import type { FlavorKeys, IBreadcrumb } from '../breadcrumbs';
import type { Segment, TLookbackMode, TPredicateMode } from '../segments';
import type { IPredicate, IPredicateGroup } from '../segments';

export enum EBrowsers {
  chrome = 'chrome',
  edge = 'edge',
  firefox = 'firefox',
  msie = 'internet Explorer',
  opera = 'opera',
  safari = 'safari',
  yahoo = 'yahoo',
}

export enum EPlatforms {
  windows = 'windows',
  macos = 'osx',
  iphone = 'iphone',
  ipad = 'ipad',
  android = 'android',
  chromeos = 'chromeos',
}

// TODO: Refactor occurrence + value interfaces into a singular interface
export interface ICompanyResponseObject {
  // The number of times this value appears on people.
  occurrence: number;
  value: string;
}

export interface IGeoIP {
  lat: number;
  lng: number;
  postal_code: string;
  timezone: string;
  locality: string;
  continent_code: string;
  country_code: string;
  country_name: string;
  administrative_area_name: string;
  administrative_area_code: string;
  sub_administrative_area_name: string;
  sub_administrative_area_code: string;
}

interface IPlaceholder {
  name?: string;
  color_name?: string;
  color_hex?: string;
  bird?: string;
}

export type TPersonCustomDataValue = string | string[] | number | boolean | undefined;

export interface IPersonCustomData {
  [key: string]: TPersonCustomDataValue;
}

export interface IPerson extends IBaseAttributes {
  // TODO: Once data in DB includes country_name, it should be a required field.
  address: Omit<IAddress, 'country_code'>;
  associated: string[];
  breadcrumbs: IObservableArray<IBreadcrumb>;
  cookies: string[];
  custom: IPersonCustomData;
  custom_data: IPersonCustomData;
  companies: string[];
  conv_count: number;
  date_created: string;
  date_last_heard_from: string;
  date_last_seen: string;
  date_last_session_start: string;
  /** For the `date_last_matched_activity` field to be included in the Person response the `include_date_last_matched_activity` param must be included in the query, e.g. `include_date_last_matched_activity: true` */
  date_last_matched_activity?: string;
  date_of_birth?: string;
  // Primary email address
  email: string;
  // Additional email addresses
  emails: string[];
  external_id: string;
  first_name?: string;
  last_brow?: EBrowsers | string;
  last_pform?: EPlatforms | string;
  last_name?: string;
  name?: string;
  seen_count: number;
  session_count: number;
  geoip: IGeoIP;
  occupation: string;
  source: FlavorKeys;
  phone: string;
  placeholder: IPlaceholder;
  pp_opt_outs?: {
    all: boolean;
    campaigns?: string[];
    events?: string[];
  };
  tag_ids: string[];
  reachable: boolean;
  is_archived?: boolean;
}

export interface ICreatePersonResponse extends Record<string, unknown> {
  /**
   * The ID of the newly created person.
   */
  id: string;
  /**
   * True if the person was created. If the person wasn't created, it's because they already exist. This is based on the email address.
   */
  create: boolean;
}

export interface IFetchPersonFieldValue extends Record<string, unknown> {
  occurrence: number;
  value: string;
}

export class Person extends DisplayModel<IPerson> {
  public readonly className = 'Person';

  public get constraints(): TConstraints<IPerson> {
    return {
      email: {
        email: {
          message: '^Email address must be valid.',
        },
      },
      phone: {
        phone: {
          message: '^Phone number must be valid.',
        },
      },
      emails: {
        list: {
          email: {
            message: '^Email address must be valid.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Must provide unique email addresses.',
        },
      },
    };
  }

  constructor(attributes: Partial<IPerson> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/data/people/${this.id}`, pathSuffix);
  }

  @computed
  public get name(): string {
    return this.get('name', '').trim() || this.get('placeholder', {}).name || 'Unknown Person';
  }

  @computed
  public get reachable(): boolean {
    return Boolean(this.get('reachable'));
  }

  @computed
  public get isArchived(): boolean {
    return this.get('is_archived', false);
  }

  public async getCompanies(query?: string): Promise<ICompanyResponseObject[]> {
    this.assertCollection(this.collection);

    const response = await wretch<IFetchPersonFieldValue[]>(
      this.collection.url('companies', encodeURIComponent(query ?? '')),
      {
        headers: this.collection.getHeaders(),
        method: 'GET',
      },
    );

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  /**
   * Sanitizes the response (overriding unexpected values) - ideally this would be handled in the backend
   */
  public override postFetch(response): any {
    const attributesToCastEmptyToUndefined = ['email', 'phone'];
    return attributesToCastEmptyToUndefined.reduce((updatedResponse, attr) => {
      if (updatedResponse[attr] === '') {
        return { ...updatedResponse, [attr]: undefined };
      }
      return updatedResponse;
    }, response);
  }

  public async createPerson(): Promise<ICreatePersonResponse> {
    this.assertCollection(this.collection);
    const createPersonUrl = `${this.collection?.url()}create/`;

    const response = await wretch<ICreatePersonResponse>(createPersonUrl, {
      body: JSON.stringify(this.attributes),
      headers: this.collection.getHeaders(),
      method: 'POST',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getBreadcrumbs(params: Partial<IPersonsListParams>): Promise<IBreadcrumb[]> {
    this.assertCollection(this.collection);

    // Serialize the predicates array to JSON string
    if (params.predicates) {
      params = {
        ...params,
        predicates: JSON.stringify(
          params.predicates,
        ) as unknown as IPersonsListParams['predicates'],
      };
    }

    const search = toQueryString(params);
    const getBreadcrumbsUrl = `${this.collection?.url('breadcrumbs', this.id)}?${search}`;

    const response = await wretch<IBreadcrumb[]>(getBreadcrumbsUrl, {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}

export interface IPersonsListParams extends IListParams<IPerson> {
  predicates: Array<IPredicate | IPredicateGroup>;
  mode: 'match_all' | 'match_any';
  lookback_mode: 'unbounded';
  lookback_value?: number;
  exclude: ['breadcrumbs'];
}

export interface IArchivePayload {
  mode: TPredicateMode;
  lookback_mode: TLookbackMode;
  lookback_value: string | number;
  predicates: IPredicate[];
  parent_segment?: string;
}

export class Persons extends Collection<Person> {
  public getModel(attributes: Partial<IPerson>): Person {
    return new Person(attributes);
  }

  public getClassName(): string {
    return 'persons';
  }

  /**
   * @param listId Use the useId() hook to generate a unique listID.
   */
  public override list(
    params: Partial<IPersonsListParams>,
    options: Partial<IListOptions> = {},
    listId?: string,
  ): ListResponse<Person> {
    return super.list(params, options, listId);
  }

  public override url(): string;

  /**
   * @param value (required) query
   */
  public override url(variant: 'companies', value: string): string;

  public override url(variant: 'page'): string;

  public override url(
    variant?: 'companies' | 'page' | 'breadcrumbs',
    value?: string | string[],
  ): string {
    if (
      variant &&
      ['companies'].includes(variant) &&
      (Array.isArray(value) ? !value.length : value === undefined)
    ) {
      throw new Error(`Value is required when variant is "${variant}".`);
    }

    switch (variant) {
      case 'companies':
        return `${this.getHostname()}persons/companies/values/?q=${value}`;

      // TODO: Why is the root persons/ endpoint not used for list?
      case 'page':
        return `${this.getHostname()}persons/page/`;

      case 'breadcrumbs':
        return `${this.getHostname()}persons/${value}/breadcrumbs/`;

      default:
        return super.url();
    }
  }

  private buildArchivePredicates(payload: {
    person_ids?: string[];
    segment?: Segment;
    predicates?: Array<IPredicate | IPredicateGroup>;
  }): Array<IPredicate | IPredicateGroup> {
    let predicates: Array<IPredicate | IPredicateGroup>;
    if (payload.predicates) {
      predicates = toJS(payload.predicates);
    } else if (payload.segment && !payload.person_ids?.length) {
      predicates = toJS(payload.segment.get('predicates', []));
    } else {
      predicates = [];
    }

    // Add ID filter if specific people are selected
    if (payload.person_ids?.length) {
      predicates.push({
        attr_against: 'id',
        attr_type: 'string',
        comparison: 'in',
        kind: 'attribute',
        value: payload.person_ids,
      } as IPredicate);
    }

    return predicates;
  }

  private buildArchivePayload(
    predicates: Array<IPredicate | IPredicateGroup>,
    segment?: Segment,
  ): IArchivePayload {
    const mode = segment?.get('mode') ?? 'match_any';
    const lookbackMode = segment?.get('lookback_mode') ?? 'unbounded';
    const lookbackValue = segment?.get('lookback_value');
    const parentSegment = segment?.get('parent_segment');

    const payload: IArchivePayload = {
      mode,
      lookback_mode: lookbackMode,
      lookback_value:
        lookbackValue && typeof lookbackValue === 'object'
          ? toJS(lookbackValue)
          : lookbackValue ?? 0,
      predicates,
    };

    if (parentSegment) {
      payload.parent_segment = parentSegment;
    }

    return payload;
  }

  // Archive/Unarchive
  private async mutatePersonStatus(
    action: 'archive' | 'unarchive',
    payload: {
      person_ids?: string[];
      segment?: Segment;
      predicates?: Array<IPredicate | IPredicateGroup>;
    },
  ): Promise<void> {
    const predicates = this.buildArchivePredicates(payload);
    const data = this.buildArchivePayload(predicates, payload.segment);

    // Make request
    const url = `${this.url()}${action}/`;
    const response = await wretch<IRachisMessage>(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (isWretchError(response)) {
      throw response.error;
    }
  }

  public async archiveByIds(personIds: string[]): Promise<void> {
    if (!personIds.length) {
      return;
    }

    await this.mutatePersonStatus('archive', { person_ids: personIds });
  }

  public async unarchiveByIds(personIds: string[]): Promise<void> {
    if (!personIds.length) {
      return;
    }

    await this.mutatePersonStatus('unarchive', { person_ids: personIds });
  }

  public async archiveBySegment(
    segment: Segment,
    predicates?: Array<IPredicate | IPredicateGroup>,
  ): Promise<void> {
    await this.mutatePersonStatus('archive', { segment, predicates });
  }

  public async unarchiveBySegment(
    segment: Segment,
    predicates?: Array<IPredicate | IPredicateGroup>,
  ): Promise<void> {
    await this.mutatePersonStatus('unarchive', { segment, predicates });
  }
}
